import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, RefreshCw, Plus } from 'lucide-react';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;

const Page = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
  color: #e2e8f0;
  font-family: 'Inter', 'Pretendard', sans-serif;
  padding: 20px;
`;
const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 28px;
  padding-bottom: 20px; border-bottom: 1px solid #1e2030; flex-wrap: wrap;
`;
const BackBtn = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer;
  padding: 8px; border-radius: 8px; transition: all 0.2s;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;
const TitleArea = styled.div`flex: 1; min-width: 200px;`;
const PageTitle = styled.h1`
  font-size: 22px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #10b981, #06b6d4);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const PageSub = styled.p`margin: 2px 0 0; font-size: 12px; color: #64748b;`;

const NavTabs = styled.div`display: flex; gap: 8px; margin-left: auto; flex-wrap: wrap;`;
const NavTab = styled.button`
  background: ${p => p.active ? 'rgba(16,185,129,0.15)' : 'transparent'};
  border: 1px solid ${p => p.active ? '#10b981' : '#1e2030'};
  color: ${p => p.active ? '#10b981' : '#64748b'};
  padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s;
  &:hover { border-color: #10b981; color: #10b981; }
`;

const Toolbar = styled.div`display: flex; gap: 10px; align-items: center; margin-bottom: 20px; flex-wrap: wrap;`;
const Select = styled.select`
  padding: 8px 12px; background: #111827; border: 1px solid #374151; border-radius: 8px;
  color: #e2e8f0; font-size: 13px;
  &:focus { outline: none; border-color: #10b981; }
`;
const Btn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 10px 18px; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; border: none; transition: all 0.2s;
  background: ${p => p.primary ? 'rgba(16,185,129,0.2)' : '#1e2030'};
  color: ${p => p.primary ? '#10b981' : '#94a3b8'};
  border: 1px solid ${p => p.primary ? '#10b981' : '#374151'};
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Pipeline = styled.div`display: flex; gap: 0; margin-bottom: 24px; animation: ${fadeIn} 0.4s ease;`;
const PipeStep = styled.div`
  flex: 1; background: #111827; padding: 16px; text-align: center; border: 1px solid #1e2030;
  &:first-child { border-radius: 12px 0 0 12px; }
  &:last-child { border-radius: 0 12px 12px 0; }
`;
const PipeNum = styled.div`font-size: 28px; font-weight: 800; color: ${p => p.color || '#e2e8f0'};`;
const PipeLabel = styled.div`font-size: 11px; color: #64748b; margin-top: 4px;`;
const PipeArrow = styled.div`display: flex; align-items: center; color: #374151; font-size: 18px;`;

const Table = styled.div`background: #111827; border: 1px solid #1e2030; border-radius: 14px; overflow-x: auto; animation: ${fadeIn} 0.5s ease;`;
const TH = styled.div`
  display: grid; grid-template-columns: 60px 1fr 100px 100px 80px 80px 80px; gap: 8px;
  padding: 12px 16px; background: #0d1117; font-size: 10px; color: #64748b; font-weight: 700; text-transform: uppercase; min-width: 700px;
`;
const TR = styled.div`
  display: grid; grid-template-columns: 60px 1fr 100px 100px 80px 80px 80px; gap: 8px;
  padding: 12px 16px; border-bottom: 1px solid #1e2030; font-size: 12px; min-width: 700px; transition: background 0.15s;
  &:hover { background: #1a1f2e; }
  &:last-child { border-bottom: none; }
`;

const SignalBadge = styled.span`
  display: inline-block; font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 6px;
  background: ${p =>
    p.type === 'SYS2_BREAKOUT' ? 'rgba(6,182,212,0.15)' :
    p.type === 'SYS1_BREAKOUT' ? 'rgba(16,185,129,0.15)' :
    p.type?.includes('READY') ? 'rgba(245,158,11,0.15)' :
    'rgba(100,116,139,0.15)'};
  color: ${p =>
    p.type === 'SYS2_BREAKOUT' ? '#06b6d4' :
    p.type === 'SYS1_BREAKOUT' ? '#10b981' :
    p.type?.includes('READY') ? '#f59e0b' :
    '#64748b'};
`;

function TurtleScreenerPage() {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [market, setMarket] = useState('ALL');
  const [running, setRunning] = useState(false);
  const [lastDate, setLastDate] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const latest = await apiService.getTurtleScreener();
        if (latest?.results) {
          setResults(latest.results);
          setPipeline(latest.pipeline || {});
          setLastDate(latest.date || '');
        }
        const hist = await apiService.getTurtleScreenerHistory();
        setHistory(hist || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const handleRun = async () => {
    setRunning(true);
    setResults([]);
    try {
      const res = await apiService.runTurtleScreener({ market });
      if (res.success && res.result) {
        setPipeline(res.result.pipeline || {});
        setResults(res.result.results || []);
        setLastDate(res.result.date || '');
        alert('🚀 스크리닝이 완료되었으며 상위 종목이 감시목록에 자동 편입되었습니다!');
      }
    } catch (e) {
      alert('스크리닝 오류: ' + e.message);
    } finally {
      setRunning(false);
    }
  };

  const addToWatchlist = async (item) => {
    try {
      const config = await apiService.getTurtleConfig();
      const exists = (config.watchlist || []).find(w => w.symbol === item.symbol);
      if (exists) {
        alert(`${item.name}은 이미 감시목록에 있습니다.`);
        return;
      }
      const updated = [...(config.watchlist || []), { symbol: item.symbol, name: item.name, market: item.market || 'KOSPI' }];
      await apiService.updateTurtleConfig({ watchlist: updated });
      alert(`✅ ${item.name}을 감시목록에 수동으로 추가했습니다.`);
    } catch (e) {
      alert('오류: ' + e.message);
    }
  };

  const fmt = n => n != null ? Number(n).toLocaleString() : '-';

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs/turtle')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>🔍 터틀 종목 스크리너</PageTitle>
          <PageSub>20일(SYS1) / 55일(SYS2) 신고가 돌파 임박 및 돌파 종목 자동 발굴 (매일 아침 08:00 자동 실행)</PageSub>
        </TitleArea>
        <NavTabs>
          <NavTab onClick={() => navigate('/labs/turtle')}>대시보드</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/positions')}>포지션</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/journal')}>거래일지</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/multi-sim')}>멀티 시뮬</NavTab>
          <NavTab active onClick={() => navigate('/labs/turtle/screener')}>스크리너</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/settings')}><Settings size={13} /></NavTab>
        </NavTabs>
      </TopBar>

      <Toolbar>
        <Select value={market} onChange={e => setMarket(e.target.value)}>
          <option value="ALL">전체 시장</option>
          <option value="KOSPI">KOSPI</option>
          <option value="KOSDAQ">KOSDAQ</option>
        </Select>
        {lastDate && <span style={{ fontSize: 12, color: '#64748b' }}>마지막 스캔: {lastDate}</span>}
        <Btn primary onClick={handleRun} disabled={running}>
          <RefreshCw size={14} style={{ animation: running ? 'spin 1s linear infinite' : 'none' }} />
          {running ? '스크리닝 중 (1~2분 소요)...' : '🔄 스크리닝 수동 실행'}
        </Btn>
      </Toolbar>

      {(pipeline.totalScanned > 0) && (
        <Pipeline>
          <PipeStep><PipeNum color="#94a3b8">{pipeline.totalScanned || 0}</PipeNum><PipeLabel>전체 스캔</PipeLabel></PipeStep>
          <PipeArrow>→</PipeArrow>
          <PipeStep><PipeNum color="#f59e0b">{pipeline.sys1Ready || 0}</PipeNum><PipeLabel>SYS1 대기 (20일 고가 3% 이내)</PipeLabel></PipeStep>
          <PipeArrow>→</PipeArrow>
          <PipeStep><PipeNum color="#f59e0b">{pipeline.sys2Ready || 0}</PipeNum><PipeLabel>SYS2 대기 (55일 고가 3% 이내)</PipeLabel></PipeStep>
          <PipeArrow>→</PipeArrow>
          <PipeStep><PipeNum color="#10b981">{pipeline.sys1Breakout || 0}</PipeNum><PipeLabel>SYS1 돌파</PipeLabel></PipeStep>
          <PipeArrow>→</PipeArrow>
          <PipeStep><PipeNum color="#06b6d4">{pipeline.sys2Breakout || 0}</PipeNum><PipeLabel>SYS2 돌파</PipeLabel></PipeStep>
        </Pipeline>
      )}

      {results.length > 0 ? (
        <Table>
          <TH><span>추가</span><span>종목</span><span>신호</span><span>현재가</span><span>20일 고가</span><span>55일 고가</span><span>ATR(N)</span></TH>
          {results.map((r, i) => (
            <TR key={r.symbol || i}>
              <div>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', padding: 2 }}
                  onClick={() => addToWatchlist(r)} title="감시목록에 추가하기">
                  <Plus size={16} />
                </button>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                <div style={{ fontSize: 10, color: '#475569' }}>{r.symbol} · {r.market}</div>
              </div>
              <div>
                <SignalBadge type={r.signal}>{r.signal}</SignalBadge>
              </div>
              <span style={{ fontWeight: 600, color: '#e2e8f0' }}>{fmt(r.currentPrice)}</span>
              <span style={{ color: r.distToHigh20.includes('-') ? '#f59e0b' : '#94a3b8' }}>
                {fmt(r.high20)} ({r.distToHigh20})
              </span>
              <span style={{ color: r.distToHigh55.includes('-') ? '#f59e0b' : '#94a3b8' }}>
                {fmt(r.high55)} ({r.distToHigh55})
              </span>
              <span style={{ color: '#06b6d4' }}>{fmt(r.N)}</span>
            </TR>
          ))}
        </Table>
      ) : !running && (
        <div style={{ textAlign: 'center', padding: 60, color: '#475569', background: '#111827', borderRadius: 14 }}>
          스크리닝 결과가 없습니다.
        </div>
      )}

      {history.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#64748b', marginBottom: 12 }}>📋 최근 스크리닝 이력</h3>
          {history.slice(0, 5).map(h => (
            <div key={h.id} style={{ display: 'flex', gap: 16, padding: '8px 0', borderBottom: '1px solid #1e2030', fontSize: 12, color: '#94a3b8' }}>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{h.date}</span>
              <span>스캔: {h.pipeline?.totalScanned || 0}종목</span>
              <span>SYS1 돌파: <span style={{ color: '#10b981' }}>{h.pipeline?.sys1Breakout || 0}</span></span>
              <span>SYS2 돌파: <span style={{ color: '#06b6d4' }}>{h.pipeline?.sys2Breakout || 0}</span></span>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}

export default TurtleScreenerPage;
